import { ItemSchemaStream } from './item-schema-stream.js';
import { canonical } from './binary.js';
const playerClasses = ['','scout','sniper','soldier','demoman','medic','heavy','pyro','spy','engineer'];
const merge = (a, b) => { const out = { ...a }; for (const [key, value] of Object.entries(b)) out[key] = value && typeof value === 'object' && !Array.isArray(value) ? merge(out[key] ?? {}, value) : value; return out; };
const truth = x => x === true || Number(x) === 1;
export class EffectSchema {
    constructor(bytes) {
        if (bytes instanceof Uint8Array) { const parser=new ItemSchemaStream();for(let i=0;i<bytes.length;i+=65536)parser.push(bytes.subarray(i,i+65536));this.schema=parser.finish(); }
        else this.schema=bytes;
        if (!this.schema?.items || !this.schema.prefabs) throw new Error('Invalid TF2 item schema');
        this.items = new Map(); this.particles = new Map();
        for (const group of Object.values(this.schema.attribute_controlled_attached_particles ?? {})) for (const [id, value] of Object.entries(group)) this.particles.set(Number(id), value);
    }
    item(id) {
        if (this.items.has(id)) return this.items.get(id);
        const resolve = (value, ancestors = new Set()) => {
            let result = {};
            for (const prefab of String(value.prefab ?? '').split(/\s+/).filter(Boolean)) {
                if (ancestors.has(prefab) || ancestors.size >= 32) throw new Error('Cyclic item prefab');
                result = merge(result, resolve(this.schema.prefabs[prefab] ?? {}, new Set([...ancestors, prefab])));
            }
            return merge(result, value);
        };
        const item = resolve(this.schema.items[id] ?? {}), attributes = { ...item.static_attrs };
        for (const [name, value] of Object.entries(item.attributes ?? {})) attributes[name] = value.value;
        const result = { displayName: item.item_name ?? item.name, hidden: truth(item.hidden), rarity: item.item_rarity, attributes, weaponSlot: item.item_slot, suffix: item.particle_suffix, classname: item.item_class, tauntAttachment: item.taunt?.particle_attachment,
            tauntProps: item.taunt?.custom_taunt_prop_per_class, tauntPropScenes: item.taunt?.custom_taunt_prop_scene_per_class,
            visuals: item.visuals, red: item.visuals_red, blue: item.visuals_blu, mvm: item.visuals_mvm_boss,
            deployedBodygroupsOnly: truth(item.hide_bodygroups_deployed_only) };
        this.items.set(id, result); return result;
    }
    bodygroupRules(id, team, style = 0xffffffff) {
        const metadata = this.item(id);
        const blocks = { visuals: metadata.visuals, visuals_red: metadata.red, visuals_blu: metadata.blue, visuals_mvm_boss: metadata.mvm };
        const resolve = (name, ancestors = new Set()) => {
            if (ancestors.has(name) || ancestors.size >= 4) throw new Error('Cyclic item visuals');
            const next = new Set([...ancestors, name]); let result = {};
            for (const [key, value] of Object.entries(blocks[name] ?? {})) {
                if (key === 'use_visualsblock_as_base') result = resolve(value, next);
                else result = merge(result, { [key]: value });
            }
            return result;
        };
        // CEconEntity uses team 0 for named player groups/styles; only the
        // world-model override uses the owner's team-specific visual block.
        const base = resolve('visuals'), selected = ({ 2: 'visuals_red', 3: 'visuals_blu', 4: 'visuals_mvm_boss' })[team];
        const world = resolve(selected && blocks[selected] ? selected : 'visuals');
        const styleInfo = style !== 0xffffffff ? base.styles?.[style] : undefined;
        return { player: base.player_bodygroups ?? {}, hidden: Object.keys(styleInfo?.additional_hidden_bodygroups ?? {}),
            itemGroup: styleInfo?.bodygroup, worldGroup: Number(world.wm_bodygroup_override ?? -1),
            worldState: Number(world.wm_bodygroup_state_override ?? -1), deployedOnly: metadata.deployedBodygroupsOnly };
    }
    managedBodygroupNames() {
        if (this.bodygroupNames) return this.bodygroupNames;
        const names = new Set();
        for (const collection of [this.schema.prefabs, this.schema.items]) for (const item of Object.values(collection))
            for (const block of ['visuals','visuals_red','visuals_blu','visuals_mvm_boss']) {
                const visual = item[block];
                for (const [name, state] of Object.entries(visual?.player_bodygroups ?? {})) if (Number(state) === 1) names.add(name);
                for (const style of Object.values(visual?.styles ?? {})) for (const name of Object.keys(style.additional_hidden_bodygroups ?? {})) names.add(name);
            }
        return this.bodygroupNames = [...names];
    }
    tauntProp(item, playerClass) {
        const name = playerClasses[playerClass], metadata = this.item(item);
        // A separately animated prop is a real server entity. Only props without
        // their own scene replace the third-person weapon model on the client.
        if (!name || metadata.tauntPropScenes?.[name]) return null;
        const model = metadata.tauntProps?.[name];
        if (!model) return null;
        const path = canonical(model);
        if (!path.endsWith('.mdl')) throw new Error('Invalid taunt prop model');
        return path;
    }
    particle(id, team, suffix) {
        const p = this.particles.get(Number(id)); if (!p?.system) return null;
        let name = p.system.replace(/_teamcolor_(red|blue)/g, team === 3 ? '_teamcolor_blue' : '_teamcolor_red');
        if (truth(p.use_suffix_name) && suffix) name += '_' + suffix;
        return { name, attachmentName: p.attachment, attachType: p.attachment ? 4 : truth(p.attach_to_rootbone) ? 6 : 1, fallbackAttachType: truth(p.attach_to_rootbone) ? 6 : 1,
            refire: Math.max(0, Number(p.refire_time) || 0), customType: Number(p.custom_type) || 0,
            controlPoints: Object.entries(p).filter(([key]) => /^control_point_[1-9]\d*$/.test(key)).map(([key, name]) => ({ id: Number(key.slice(14)), attachmentName: name })) };
    }
    visualParticles(metadata, team) {
        const blocks={visuals:metadata.visuals,visuals_red:metadata.red,visuals_blu:metadata.blue,visuals_mvm_boss:metadata.mvm};
        const selected=({2:'visuals_red',3:'visuals_blu',4:'visuals_mvm_boss'})[team];
        const resolve=(name,ancestors=new Set())=>{
            if(ancestors.has(name)||ancestors.size>=4)throw new Error('Cyclic item visuals');
            const next=new Set([...ancestors,name]);let result=[];
            for(const [key,value] of Object.entries(blocks[name]??{})) {
                // Native copies the named, already parsed visuals then appends
                // entries. Numeric keys in two attachment blocks never collide.
                if(key==='use_visualsblock_as_base')result=resolve(value,next);
                else if(key==='attached_particlesystems') {
                    for(const data of Object.values(value))if(data.system)result.push({name:data.system,attachmentName:data.attachment,
                        attachType:data.attachment?4:truth(data.attach_to_rootbone)?6:1,fallbackAttachType:truth(data.attach_to_rootbone)?6:1,customType:0});
                } else if(key==='attached_particles') {
                    for(const data of Object.values(value)){const particle=this.particle(data.system,team,metadata.suffix);if(particle)result.push(particle);}
                }
                else if((key==='custom_particlesystem'||key==='custom_particlesystem2')&&value.system)result.push({name:value.system,customType:key==='custom_particlesystem'?1:2});
            }
            return result;
        };
        return resolve(selected&&blocks[selected]?selected:'visuals');
    }
    get({ item = 0, team = 2, quality = 0, unusual = 0, staticParticle = 0, taunt = 0, tauntItem = 0, killstreakEffect = 0 } = {}) {
        const metadata = this.item(item), cosmetics = [];
        const add = id => { const p = this.particle(id, team, metadata.suffix); if (p && !p.customType && !cosmetics.some(e => e.name === p.name)) cosmetics.push(p); };
        if (!Number(metadata.attributes['throwable particle trail only'])) add(unusual || metadata.attributes['attach particle effect']);
        add(staticParticle || metadata.attributes['attach particle effect static']);
        if ([7, 9].includes(quality)) add(4);
        const particles=this.visualParticles(metadata,team);
        for(const effect of particles)if(!effect.customType&&!cosmetics.some(old=>old.name===effect.name&&old.attachmentName===effect.attachmentName))cosmetics.push(effect);
        const custom=particles.find(effect=>effect.customType===1)?.name;
        const tauntEffect = this.particle(taunt, team);
        if (tauntEffect) { tauntEffect.attachmentName = this.item(tauntItem).tauntAttachment; tauntEffect.attachType = tauntEffect.attachmentName ? 4 : 1; }
        const eye = killstreakEffect || Number(metadata.attributes['killstreak effect']) || 0;
        return { ...metadata, item, cosmetics, killstreakLow: this.particle(eye,team), killstreakHigh: this.particle(eye+20000,team), tauntEffect, eventParticleOverride: custom, customBeams: particles.filter(effect=>effect.customType===1).map(effect=>effect.name) };
    }
}
